import { Vue, Component, Prop } from 'vue-property-decorator'
import { visibleObserver } from '@/lib/visible-observer'
import numberToFixed from '@/helpers/numberToFixed'

@Component
export default class ImageCrop extends Vue {
  @Prop({ type: String, default: 'top' }) private readonly valign!: string
  @Prop({ type: String, default: 'center' }) private readonly align!: string

  @Prop({ type: Boolean, default: false }) private readonly contain!: boolean
  @Prop({ type: Boolean, default: false }) private readonly circle!: boolean
  @Prop({ type: Number }) private readonly aspectRatio?: number
  @Prop({ type: Number }) private readonly height?: number
  @Prop({ type: Boolean, default: false })
  private readonly draggable!: boolean
  @Prop({ type: String })
  private readonly originalImageUrl?: string
  @Prop({ type: Number, default: 0 }) private readonly cropX!: number
  @Prop({ type: Number, default: 0 }) private readonly cropY!: number
  @Prop({ type: Number, default: 0 }) private readonly cropW!: number
  @Prop({ type: Number, default: 0 }) private readonly cropH!: number

  private croppedImageUrl = ''
  private isCroppedImageLoaded = false
  private isDestroyed = false

  private get localAspectRatio() {
    if (this.height) {
      return
    }

    if (this.circle) {
      return 1
    }

    if (!this.aspectRatio) {
      if (this.cropW && this.cropH) {
        return numberToFixed(this.cropW / this.cropH, 3)
      }

      if (!this.isCroppedImageLoaded) {
        return 1.5
      }
    }

    return this.aspectRatio
  }

  private async onLoad() {
    this.isCroppedImageLoaded = true

    this.$emit('ready')
  }

  private mounted() {
    let imageEl: HTMLImageElement | null = null

    const generateCroppedImage = () => {
      const $el = this.$el as HTMLDivElement | undefined

      if (this.isDestroyed || !$el || !imageEl) {
        return
      }

      const canvas = document.createElement('canvas')
      const canvasCtx = canvas.getContext('2d')

      if (!canvasCtx) {
        return
      }

      canvas.width = Math.min($el.clientWidth + 200, this.cropW)
      canvas.height = canvas.width * (this.cropH / this.cropW)

      canvasCtx.imageSmoothingQuality = 'high'
      canvasCtx.imageSmoothingEnabled = true

      canvasCtx?.drawImage(
        imageEl,
        this.cropX,
        this.cropY,
        this.cropW,
        this.cropH,
        0,
        0,
        canvas.width,
        canvas.height
      )

      canvas.toBlob((imageBlob) => {
        URL.revokeObjectURL(this.croppedImageUrl)

        if (imageBlob && !this.isDestroyed) {
          this.croppedImageUrl = URL.createObjectURL(imageBlob)
        }

        imageEl?.removeEventListener('load', generateCroppedImage)
        imageEl?.remove()
        canvas.remove()
        imageEl = null
      })
    }

    let requestAnimationFrameId = 0

    this.$watch(
      () => {
        return [
          this.originalImageUrl,
          this.cropH,
          this.cropW,
          this.cropX,
          this.cropY,
        ].join('-')
      },
      () => {
        visibleObserver.unobserve(this.$el)

        const originalImageUrl = this.originalImageUrl

        if (!originalImageUrl) {
          this.isCroppedImageLoaded = false
          this.croppedImageUrl = ''
          return
        }

        visibleObserver.observe(this.$el, () => {
          window.cancelAnimationFrame(requestAnimationFrameId)

          requestAnimationFrameId = window.requestAnimationFrame(() => {
            this.isCroppedImageLoaded = false

            imageEl?.removeEventListener('load', generateCroppedImage)
            imageEl?.remove()

            imageEl = new Image()

            imageEl.addEventListener('load', generateCroppedImage)
            imageEl.crossOrigin = 'anonymous'
            imageEl.src = originalImageUrl
          })
        })
      },
      {
        immediate: true,
      }
    )

    this.$once('hook:beforeDestroy', () => {
      this.isDestroyed = true
      visibleObserver.unobserve(this.$el)

      window.cancelAnimationFrame(requestAnimationFrameId)
      imageEl?.removeEventListener('load', generateCroppedImage)
      imageEl?.remove()
      imageEl = null
      URL.revokeObjectURL(this.croppedImageUrl)
    })
  }
}
